import { z } from 'zod';

import { ElementCategory } from './element.js';
import { EvidenceSpan } from './production-plan.js';
import type { ItemState } from './scene-breakdown.js';
import { ItemEvidence, StoredCoverageStatus } from './scene-breakdown.js';

/**
 * KEY TAKEAWAYS — an AI-assisted reading of what matters most in a scene, written FROM its
 * breakdown, never in place of it (Production Workspace 2.0, owner decisions D2 and D5,
 * corrections C3, C6 and S3; design record
 * `docs/superpowers/specs/2026-11-04-workspace-scene-breakdown-design.md`).
 *
 * - The model is given the scene and the items it already has, each under a short ref, and may
 *   only point at those refs. The server keeps a takeaway only when its refs are real and its
 *   quote is found in that scene.
 * - A reading is never a plan and never a fact: the card says so, and the wording guardrail keeps
 *   safety and legal statements at "a specialist assessment may be needed".
 * - A reading is versioned against what it was given. When the text, the items, the coverage or
 *   the production plan change materially, it reads "out of date"; a person accepting an item
 *   does not age it (an explicit rule, `takeawayFreshness`).
 */

export const TakeawayKind = z.enum([
  'COMPLEXITY',
  'OPEN_DECISION',
  'DEPENDENCY',
  'CONTINUITY',
  'SPECIALIST_REVIEW',
  'LOGISTICS',
]);
export type TakeawayKind = z.infer<typeof TakeawayKind>;

/**
 * - `READ`: answered, at least one takeaway kept.
 * - `READ_EMPTY`: answered, nothing stood out (or nothing survived validation).
 * - `UNREAD`: the scene was left out of the answer, twice.
 * - `FAILED`: asked, no usable answer.
 * - `UNAVAILABLE`: the transport cannot read takeaways (the direct Gemini path).
 */
export const TakeawayReadingStatus = z.enum(['READ', 'READ_EMPTY', 'UNREAD', 'FAILED', 'UNAVAILABLE']);
export type TakeawayReadingStatus = z.infer<typeof TakeawayReadingStatus>;

export const SCENE_TAKEAWAYS_SCHEMA_VERSION = '1';
export const SCENE_TAKEAWAYS_PROMPT_KEY = 'scene_takeaways_core';

/** Caps applied by the server after parsing: the provider schema carries no length keywords. */
export const MAX_TAKEAWAYS_PER_SCENE = 4;
export const MAX_TAKEAWAY_TEXT_CHARS = 240;
export const MAX_TAKEAWAY_REFS = 6;
/** Items sent per scene; a scene with more sends the first ones by category order and says so. */
export const MAX_TAKEAWAY_INPUT_ITEMS = 60;

// ---------------------------------------------------------------------------------------------
// The scope of a SCRIPT_ANALYSIS (D5)
// ---------------------------------------------------------------------------------------------

/**
 * What a `SCRIPT_ANALYSIS` reads. `BREAKDOWN` (the default, and what an absent `scope` means) is
 * the locations and the scene breakdown, exactly as before takeaways existed; `TAKEAWAYS` is the
 * Key Takeaways of the selected scenes, from the breakdown they already have. Not an operation
 * type: the same operation, priced from its own usage plan, so the AI credits contract is
 * unchanged.
 */
export const AnalysisScope = z.enum(['BREAKDOWN', 'TAKEAWAYS']);
export type AnalysisScope = z.infer<typeof AnalysisScope>;

/**
 * The refusals of a `TAKEAWAYS` request, before anything is reserved or queued.
 * - `TAKEAWAYS_OFF` (409): `SCENE_TAKEAWAYS` is off in this environment.
 * - `TAKEAWAYS_NEED_BREAKDOWN` (409): a selected scene was never read by a finished breakdown
 *   (`scene.analysed_at` unset); the body names those scenes as `sceneIds`.
 */
export const TAKEAWAYS_OFF_CODE = 'TAKEAWAYS_OFF';
export const TAKEAWAYS_NEED_BREAKDOWN_CODE = 'TAKEAWAYS_NEED_BREAKDOWN';

/**
 * A takeaways run's `failure_code` (its EXTRACT stage and its run), never an element or location
 * code: a takeaways run reads no locations and no elements.
 * - `TAKEAWAYS_NOT_READ`: some or all selected scenes have no new reading (left out twice, a failed
 *   request, a reading that could not be saved); `failedUnits` counts them.
 * - `TAKEAWAYS_UNAVAILABLE`: the transport cannot read takeaways (no AI Router configured).
 */
export const TAKEAWAYS_NOT_READ_FAILURE_CODE = 'TAKEAWAYS_NOT_READ';
export const TAKEAWAYS_UNAVAILABLE_FAILURE_CODE = 'TAKEAWAYS_UNAVAILABLE';

/**
 * THE STAGE A TAKEAWAYS RUN WRITES, never `EXTRACT`. A takeaways run is a pipeline run like a
 * breakdown, but it is not the breakdown: every reader of "the latest analysis" — the progress
 * answer, `extractionHasRun`, the PARTIAL notices, the version's analysis fallback, the Overview —
 * asks for an `EXTRACT` stage or skips runs that carry this one. The row is written QUEUED with
 * the run itself (`ScriptsService.startExtractionRun`), so a takeaways run is never, even for an
 * instant, a run without its marker.
 */
export const TAKEAWAYS_STAGE = 'TAKEAWAYS';

/**
 * A `continuesOperationId` must name an operation of the SAME scope: a breakdown continues a
 * stopped breakdown, takeaways continue stopped takeaways (409, before anything is reserved).
 */
export const CONTINUATION_SCOPE_MISMATCH_CODE = 'CONTINUATION_SCOPE_MISMATCH';

// ---------------------------------------------------------------------------------------------
// The model's answer
// ---------------------------------------------------------------------------------------------

/**
 * What the model returns, per scene it was sent. Types, enums and `required` only — the providers
 * refuse length keywords in a JSON schema (production plan, 2026-11-05) — so the caps above are
 * applied by the server, never by the schema.
 */
export const SceneTakeawaysOutput = z.object({
  scenes: z.array(
    z.object({
      sceneRef: z.number().int(),
      takeaways: z.array(
        z.object({
          kind: TakeawayKind,
          text: z.string(),
          /** Refs from the input (`i3`, `r1`, `o2`), never names. */
          refs: z.array(z.string()),
          /** The block the quote is from (`b2`), as rendered. */
          block: z.string(),
          quote: z.string(),
        }),
      ),
    }),
  ),
});
export type SceneTakeawaysOutput = z.infer<typeof SceneTakeawaysOutput>;

// ---------------------------------------------------------------------------------------------
// What is stored
// ---------------------------------------------------------------------------------------------

/** What a takeaway points at, resolved from the input ref at write. */
export const TakeawayRef = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('ITEM'), itemKey: z.string() }),
  z.object({ kind: z.literal('REQUIREMENT'), requirementKey: z.string() }),
  z.object({ kind: z.literal('OPTION'), optionKey: z.string() }),
]);
export type TakeawayRef = z.infer<typeof TakeawayRef>;

export const StoredTakeaway = z.object({
  kind: TakeawayKind,
  text: z.string(),
  refs: z.array(TakeawayRef),
  /** The verified quote, scene-relative; null when the takeaway rests on its refs alone. */
  evidence: EvidenceSpan.nullable(),
  /**
   * For an `OPEN_DECISION` only: whether each cited item was on the sheet when it was read. The
   * one place review state is material (S3): once the decision it names is made, that takeaway
   * says so.
   */
  citedConfirmation: z.record(z.string(), z.boolean()),
});
export type StoredTakeaway = z.infer<typeof StoredTakeaway>;

/**
 * The SEMANTIC basis of a reading (S3): what it concluded from. Compared field by field, so the
 * screen can say what changed. Provenance (prompt, model, schema, everything sent) is kept apart
 * in `input_hash` and never ages a reading.
 */
export const TakeawayBasis = z.object({
  /** sha256 of the scene's text as read. */
  bodyHash: z.string(),
  /** The items it was given that were not "not needed", sorted by key. */
  items: z.array(
    z.object({
      key: z.string(),
      category: ElementCategory,
      name: z.string(),
      quantity: z.number().int().nullable(),
    }),
  ),
  /** The stored coverage per category it was given (never the derived OUT_OF_DATE). */
  coverage: z.partialRecord(ElementCategory, StoredCoverageStatus),
  /** The production plan's input, or null when the plan was off or had nothing for the scene. */
  plan: z
    .object({
      requirements: z.array(
        z.object({
          requirementKey: z.string(),
          semanticFingerprint: z.string(),
          reviewStatus: z.string(),
          basisStatus: z.string(),
        }),
      ),
      options: z.array(z.object({ optionKey: z.string(), method: z.string(), status: z.string() })),
    })
    .nullable(),
});
export type TakeawayBasis = z.infer<typeof TakeawayBasis>;

// ---------------------------------------------------------------------------------------------
// Freshness (C6, S3)
// ---------------------------------------------------------------------------------------------

export const FreshnessReason = z.enum(['TEXT_CHANGED', 'ITEMS_CHANGED', 'COVERAGE_CHANGED', 'PLAN_CHANGED']);
export type FreshnessReason = z.infer<typeof FreshnessReason>;

/**
 * One takeaway's own warning, apart from the reading's.
 * - `ITEM_NOT_NEEDED`: an item it cites is marked not needed now (or is gone).
 * - `DECISION_MADE`: an `OPEN_DECISION` whose cited items have been decided since.
 */
export const TakeawayFlag = z.enum(['ITEM_NOT_NEEDED', 'DECISION_MADE']);
export type TakeawayFlag = z.infer<typeof TakeawayFlag>;

function stableItems(items: TakeawayBasis['items']): string {
  return JSON.stringify(
    [...items]
      .sort((a, b) => a.key.localeCompare(b.key))
      .map((i) => [i.key, i.category, i.name, i.quantity]),
  );
}

function stableRecord(record: Readonly<Record<string, string | undefined>>): string {
  return JSON.stringify(
    Object.entries(record)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b)),
  );
}

function stablePlan(plan: TakeawayBasis['plan']): string {
  if (plan === null) return 'null';
  return JSON.stringify({
    r: [...plan.requirements]
      .sort((a, b) => a.requirementKey.localeCompare(b.requirementKey))
      .map((r) => [r.requirementKey, r.semanticFingerprint, r.reviewStatus, r.basisStatus]),
    // Only a SELECTED option is a decision a takeaway can rest on; proposed ones come and go.
    o: plan.options
      .filter((o) => o.status === 'SELECTED')
      .sort((a, b) => a.optionKey.localeCompare(b.optionKey))
      .map((o) => [o.optionKey, o.method]),
  });
}

/**
 * Is a reading still about the scene as it is now? THE one rule, applied by the server; the SPA
 * renders its answer.
 *
 * Material, and named: the scene's text, the included items (identity, category, name, quantity;
 * an item marked not needed or restored), the coverage per category, and the production plan's
 * requirements and selected methods when the reading was given them (or when a plan exists now
 * and did not then). NOT material, by rule: an item moving between "awaiting your decision" and
 * "on the sheet" — except for an `OPEN_DECISION` that cites it, which is flagged on its own.
 */
export function takeawayFreshness(
  stored: { readonly basis: TakeawayBasis; readonly takeaways: readonly StoredTakeaway[] },
  current: { readonly basis: TakeawayBasis; readonly itemStates: Readonly<Record<string, ItemState>> },
): { outOfDate: boolean; reasons: FreshnessReason[]; flags: (TakeawayFlag | null)[] } {
  const reasons: FreshnessReason[] = [];

  if (stored.basis.bodyHash !== current.basis.bodyHash) reasons.push('TEXT_CHANGED');
  if (stableItems(stored.basis.items) !== stableItems(current.basis.items)) reasons.push('ITEMS_CHANGED');
  if (stableRecord(stored.basis.coverage) !== stableRecord(current.basis.coverage)) reasons.push('COVERAGE_CHANGED');

  const planAppeared =
    stored.basis.plan === null && current.basis.plan !== null && current.basis.plan.requirements.length > 0;
  const planChanged =
    stored.basis.plan !== null && current.basis.plan !== null && stablePlan(stored.basis.plan) !== stablePlan(current.basis.plan);
  if (planAppeared || planChanged) reasons.push('PLAN_CHANGED');

  const flags = stored.takeaways.map((t): TakeawayFlag | null => {
    const items = t.refs.filter((r): r is Extract<TakeawayRef, { kind: 'ITEM' }> => r.kind === 'ITEM');
    if (items.some((r) => (current.itemStates[r.itemKey] ?? 'NOT_NEEDED') === 'NOT_NEEDED')) return 'ITEM_NOT_NEEDED';
    if (t.kind === 'OPEN_DECISION') {
      const decided = items.some((r) => {
        const was = t.citedConfirmation[r.itemKey];
        return was !== undefined && was !== (current.itemStates[r.itemKey] === 'ON_SHEET');
      });
      if (decided) return 'DECISION_MADE';
    }
    return null;
  });

  return { outOfDate: reasons.length > 0, reasons, flags };
}

// ---------------------------------------------------------------------------------------------
// The view
// ---------------------------------------------------------------------------------------------

export const TakeawayRefView = z.object({
  kind: z.enum(['ITEM', 'REQUIREMENT', 'OPTION']),
  key: z.string(),
  label: z.string(),
  category: ElementCategory.nullable(),
  /** False when what it cites is no longer there (removed, or the plan is off). */
  present: z.boolean(),
});
export type TakeawayRefView = z.infer<typeof TakeawayRefView>;

export const SceneTakeawayView = z.object({
  kind: TakeawayKind,
  text: z.string(),
  refs: z.array(TakeawayRefView),
  evidence: ItemEvidence.nullable(),
  flag: TakeawayFlag.nullable(),
});
export type SceneTakeawayView = z.infer<typeof SceneTakeawayView>;

/**
 * THE TAKEAWAYS CARD'S POLL (`GET /v1/script-versions/:id/takeaways-progress`): the version's latest
 * takeaways run, or null when it has none. Kept apart from the breakdown's progress answer, which
 * never reports a takeaways run.
 *
 * `scenes` are the readings THIS run saved, one per scene it read, with the reading's status; empty
 * while the run is QUEUED or RUNNING (readings are saved when the reading ends). `totalUnits` is the
 * number of scenes the run was asked to read, known from the moment it is queued.
 */
export const TakeawaysProgress = z.object({
  runId: z.string(),
  /** The run's status: QUEUED, RUNNING, COMPLETED, PARTIAL or FAILED. */
  status: z.string(),
  /** `TAKEAWAYS_NOT_READ`, `TAKEAWAYS_UNAVAILABLE`, `STOPPED_AT_CAP`, `EXTRACT_INTERRUPTED`, …; null when none. */
  failureCode: z.string().nullable(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  totalUnits: z.number().int().nullable(),
  completedUnits: z.number().int(),
  failedUnits: z.number().int(),
  scenes: z.array(z.object({ sceneId: z.string(), status: TakeawayReadingStatus })),
});
export type TakeawaysProgress = z.infer<typeof TakeawaysProgress>;

export const SceneTakeawaysView = z.object({
  readingId: z.string(),
  status: TakeawayReadingStatus,
  readAt: z.string(),
  model: z.string(),
  promptVersion: z.string(),
  outOfDate: z.boolean(),
  reasons: z.array(FreshnessReason),
  takeaways: z.array(SceneTakeawayView),
  /** Earlier readings of this scene, kept (C6). */
  earlierReadings: z.number().int(),
});
export type SceneTakeawaysView = z.infer<typeof SceneTakeawaysView>;
